import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  useConfigCategories, useCreateConfigItem, useUpdateConfigItem, useDeleteConfigItem, useSeedConfig,
  type ConfigItem, type ConfigCategory,
} from '@/hooks/useConfig'
import {
  Plus, Edit2, Trash2, X, Check, Save, Loader2, GripVertical, Eye, EyeOff, ChevronDown, ChevronUp, RefreshCw,
  MapPin, BarChart3, GitBranch, Briefcase, UserCheck, Layers, AlertTriangle, Package,
} from 'lucide-react'

const CATEGORY_ICONS: Record<string, typeof MapPin> = {
  lead_origin: MapPin,
  lead_status: BarChart3,
  opportunity_stage: GitBranch,
  proposal_status: Briefcase,
  contact_role: UserCheck,
  journey_channel: Layers,
  journey_type: Package,
  loss_reason: AlertTriangle,
}

const CATEGORY_COLORS: Record<string, string> = {
  lead_origin: 'purple',
  lead_status: 'amber',
  opportunity_stage: 'indigo',
  proposal_status: 'rose',
  contact_role: 'cyan',
  journey_channel: 'teal',
  journey_type: 'emerald',
  loss_reason: 'red',
}

function colorClasses(name: string) {
  const map: Record<string, string> = {
    purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
    amber: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400',
    rose: 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400',
    cyan: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400',
    teal: 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
    red: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
  }
  return map[name] || map.purple
}

export default function ConfigCRUD() {
  const { data: categories, isLoading } = useConfigCategories()
  const seedMutation = useSeedConfig()
  const [expandedCat, setExpandedCat] = useState<string | null>(null)

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
  }

  const isEmpty = !categories || categories.every(c => c.items.length === 0)

  return (
    <div className="space-y-4">
      {isEmpty && (
        <div className="text-center py-8 border rounded-lg bg-muted/30">
          <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">Nenhum cadastro encontrado. Clique abaixo para popular os dados iniciais.</p>
          <Button onClick={() => seedMutation.mutate()} isLoading={seedMutation.isPending}>
            <RefreshCw className="h-4 w-4 mr-2" /> Inicializar Cadastros
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories?.map(cat => (
          <CategoryCard
            key={cat.category}
            category={cat}
            isExpanded={expandedCat === cat.category}
            onToggle={() => setExpandedCat(expandedCat === cat.category ? null : cat.category)}
          />
        ))}
      </div>
    </div>
  )
}

function CategoryCard({ category, isExpanded, onToggle }: { category: ConfigCategory; isExpanded: boolean; onToggle: () => void }) {
  const IconComp = CATEGORY_ICONS[category.category] || Package
  const color = CATEGORY_COLORS[category.category] || 'purple'
  const createMutation = useCreateConfigItem()
  const updateMutation = useUpdateConfigItem()
  const deleteMutation = useDeleteConfigItem()

  const [showAdd, setShowAdd] = useState(false)
  const [editItem, setEditItem] = useState<ConfigItem | null>(null)
  const [form, setForm] = useState({ key: '', label: '', color: '#6B7280', description: '' })
  const [confirmDeleteItem, setConfirmDeleteItem] = useState<ConfigItem | null>(null)

  const activeCount = category.items.filter(i => i.is_active).length

  const handleAdd = async () => {
    if (!form.key.trim() || !form.label.trim()) return
    try {
      await createMutation.mutateAsync({
        category: category.category,
        key: form.key.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        label: form.label,
        color: form.color || null,
        description: form.description || null,
        sort_order: category.items.length * 10,
      })
      setForm({ key: '', label: '', color: '#6B7280', description: '' })
      setShowAdd(false)
    } catch {}
  }

  const handleUpdate = async () => {
    if (!editItem) return
    try {
      await updateMutation.mutateAsync({
        id: editItem.id,
        data: {
          label: form.label,
          color: form.color || null,
          description: form.description || null,
        },
      })
      setEditItem(null)
    } catch {}
  }

  const handleToggleActive = async (item: ConfigItem) => {
    await updateMutation.mutateAsync({ id: item.id, data: { is_active: !item.is_active } })
  }

  const handleDelete = async (item: ConfigItem) => {
    try {
      await deleteMutation.mutateAsync(item.id)
    } catch {}
  }

  const startEdit = (item: ConfigItem) => {
    setEditItem(item)
    setForm({ key: item.key, label: item.label, color: item.color || '#6B7280', description: item.description || '' })
    setShowAdd(false)
  }

  const startAdd = () => {
    setShowAdd(true)
    setEditItem(null)
    setForm({ key: '', label: '', color: '#6B7280', description: '' })
  }

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${isExpanded ? 'md:col-span-2' : ''}`}>
      {/* Header */}
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors text-left">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses(color)}`}>
            <IconComp className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">{category.label}</h3>
            <p className="text-xs text-muted-foreground">{activeCount} ativo{activeCount !== 1 ? 's' : ''} de {category.items.length} total</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-1 max-w-[200px]">
            {category.items.filter(i => i.is_active).slice(0, 3).map(i => (
              <span key={i.id} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: `${i.color}20`, color: i.color || '#6B7280' }}>
                {i.label}
              </span>
            ))}
            {activeCount > 3 && <span className="text-[10px] text-muted-foreground">+{activeCount - 3}</span>}
          </div>
          {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t">
          {/* Items list */}
          <div className="divide-y">
            {category.items.map(item => (
              <div key={item.id} className={`flex items-center gap-3 px-4 py-2.5 ${!item.is_active ? 'opacity-50' : ''} ${editItem?.id === item.id ? 'bg-primary/5' : 'hover:bg-muted/30'}`}>
                {editItem?.id === item.id ? (
                  /* Edit mode */
                  <div className="flex-1 flex items-center gap-2">
                    <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-7 h-7 rounded cursor-pointer border-0 p-0" />
                    <Input className="h-8 text-sm flex-1" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Label" autoFocus />
                    <Input className="h-8 text-sm w-48" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descricao (opcional)" />
                    <Button size="sm" variant="ghost" onClick={handleUpdate} disabled={updateMutation.isPending}>
                      {updateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-green-600" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditItem(null)}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                ) : (
                  /* View mode */
                  <>
                    <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: item.color || '#6B7280' }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium">{item.label}</span>
                      <span className="text-[10px] text-muted-foreground ml-2">{item.key}</span>
                      {item.description && <p className="text-xs text-muted-foreground truncate">{item.description}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {item.is_system && <span className="text-[9px] text-muted-foreground px-1.5 py-0.5 rounded bg-muted">Sistema</span>}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleToggleActive(item)} title={item.is_active ? 'Desativar' : 'Ativar'}>
                        {item.is_active ? <Eye className="h-3.5 w-3.5 text-green-600" /> : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(item)} title="Editar">
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      {!item.is_system && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setConfirmDeleteItem(item)} title="Excluir">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Add new item */}
          {showAdd ? (
            <div className="p-4 bg-muted/20 border-t">
              <div className="flex items-center gap-2">
                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-7 h-7 rounded cursor-pointer border-0 p-0" />
                <Input className="h-8 text-sm w-32" value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} placeholder="Chave (ex: novo_item)" />
                <Input className="h-8 text-sm flex-1" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Label exibido" />
                <Input className="h-8 text-sm w-48" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descricao (opcional)" />
                <Button size="sm" onClick={handleAdd} disabled={createMutation.isPending || !form.key || !form.label}>
                  {createMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}><X className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ) : (
            <div className="p-3 border-t">
              <Button size="sm" variant="outline" onClick={startAdd} className="w-full">
                <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar Item
              </Button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteItem}
        title="Excluir item de configuração?"
        message={`O item "${confirmDeleteItem?.label || ''}" será removido. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={async () => {
          if (!confirmDeleteItem) return
          await handleDelete(confirmDeleteItem)
          setConfirmDeleteItem(null)
        }}
        onCancel={() => setConfirmDeleteItem(null)}
      />
    </div>
  )
}
